import type {
    CreateEntityOptions,
    CreatePayloadArgObject,
    CreatePayloadCreatorType,
    DeleteByIdArg,
    DeleteByIdPayloadCreatorType,
    DeleteByIdReturn,
    EntityListSliceState,
    EntitySliceState,
    GetPayloadArg,
    GetPayloadCreatorReturnType,
    PayloadCreator,
    QueryAllPayloadCreatorType,
    QueryByIdPayloadArgObject,
    QueryByIdPayloadCreatorType,
    UpdateByIdPayloadCreatorType,
    UseEntities,
    UseEntity,
} from './createEntityTypes';
import type { AgnosticCondition } from '../utils/internalTypings';
import { buildUseEntities } from './createEntityUseEntitiesBuilder';
import { buildUseEntity } from './createEntityUseEntityBuilder';
import { mapListToIndexedObject } from './createEntityUtils';
import { createPaginationEntityAdapter } from './createEntityPaginationAdapter';
import { KeyNamespaces } from '../utils/getKey';
import type {
    CreateEntityMutationOptions,
    QueryParamsBaseType,
    UpdateByIdArg,
    UseCreateEntity,
    UseDeleteEntity,
    UseUpdateEntity,
} from '../paginationEntity/createPaginationEntityTypes';
import type { GetSliceId } from '../utils/sliceSelectorsTypes';

type Create<Entity, QueryParams> = (
    partialEntity: Partial<Entity>,
    options?: CreateEntityMutationOptions<QueryParams>,
) => Promise<Entity>;
type DeleteById<DeleteByIdPayloadCreator> = (
    id: DeleteByIdArg,
) => Promise<GetPayloadCreatorReturnType<DeleteByIdPayloadCreator>>;
type UpdateById<Entity> = (argWithIdAndChanges: UpdateByIdArg<Entity>) => Promise<Entity>;

export type CreateEntityReturn<
    Entity,
    QueryAllPayloadCreator,
    QueryByIdPayloadCreator,
    UpdateByIdPayloadCreator,
    CreatePayloadCreator,
    DeleteByIdPayloadCreator,
> = {
    /** A custom hook to consume a single entity, fetching is automatically handled */
    useEntity: UseEntity<Entity, GetPayloadArg<QueryByIdPayloadCreator>>;
    /** A custom hook to consume all entities, fetching is automatically handled */
    useEntities: UseEntities<Entity, GetPayloadArg<QueryAllPayloadCreator>>;
    // @ts-expect-error - invalid typing: inference works
    useUpdateEntity: UseUpdateEntity<Entity, GetPayloadArg<UpdateByIdPayloadCreator>>;
    useCreateEntity: UseCreateEntity<
        Entity,
        GetPayloadArg<CreatePayloadCreator>,
        GetPayloadArg<QueryAllPayloadCreator, QueryParamsBaseType>
    >;
    useDeleteEntity: UseDeleteEntity<
        GetPayloadCreatorReturnType<DeleteByIdPayloadCreator>,
        // @ts-expect-error - invalid typing: inference works
        GetPayloadArg<DeleteByIdPayloadCreator, any>
    >;
    queryAll: (
        arg?: GetPayloadArg<QueryAllPayloadCreator, QueryParamsBaseType>,
    ) => Promise<Entity[]>;
    queryAllIfNeeded: (
        arg?: GetPayloadArg<QueryAllPayloadCreator, QueryParamsBaseType>,
    ) => Promise<Entity[] | undefined>;
    queryById: (arg: GetPayloadArg<QueryByIdPayloadCreator>) => Promise<Entity>;
    // returns undefined when invoked while already pending
    queryByIdIfNeeded: (arg: GetPayloadArg<QueryByIdPayloadCreator>) => Promise<Entity | undefined>;
    create: Create<Entity, GetPayloadArg<QueryAllPayloadCreator, QueryParamsBaseType>>;
    deleteById: DeleteById<DeleteByIdPayloadCreator>;
    updateById: UpdateById<Entity>;
    invalidate: () => void;
    invalidateById: (id: string) => void;
    /** selectSlice exposes internal implementation
     * details, only use as a last resort in legacy code. */
    selectSlice: () => EntityListSliceState<Entity>;
    selectSliceById: (id: string) => EntitySliceState<Entity>;
    selectAll: () => Entity[];
    selectEntities: () => Record<string, Entity | undefined>;
    selectById: (id: string) => Entity | undefined;
    selectIds: () => string[];
    selectTotal: () => number;
    /**
     * Returns identification of list slice
     */
    getListSliceId: GetSliceId;
    /**
     * Returns identification of detail slice
     */
    getDetailSliceId: GetSliceId<string>;
    /** Allows to manually update entity in the cache
     *  (useful when same resource can be fetched from different endpoint)
     */
    updateCache: (entity: Entity) => void;
    /**
     * Allows to manually update list cache at once
     (useful when same resource can be fetched from different endpoint)
     */
    updateListCache: (entities: Entity[]) => void;
};

export function createEntity<
    Entity,
    QueryAllPayloadCreator extends QueryAllPayloadCreatorType<
        Entity,
        QueryParamsBaseType
    > = QueryAllPayloadCreatorType<Entity, QueryParamsBaseType>,
    QueryByIdPayloadCreator extends QueryByIdPayloadCreatorType<Entity> = PayloadCreator<
        Entity,
        string | QueryByIdPayloadArgObject
    >,
    UpdateByIdPayloadCreator extends
        UpdateByIdPayloadCreatorType<Entity> = UpdateByIdPayloadCreatorType<Entity>,
    CreatePayloadCreator extends CreatePayloadCreatorType<Entity> = PayloadCreator<
        Entity,
        Partial<Entity> | CreatePayloadArgObject<Entity>
    >,
    DeleteByIdPayloadCreator extends DeleteByIdPayloadCreatorType = PayloadCreator<
        DeleteByIdReturn,
        DeleteByIdArg
    >,
>(
    config: CreateEntityOptions<
        Entity,
        QueryAllPayloadCreator,
        QueryByIdPayloadCreator,
        UpdateByIdPayloadCreator,
        CreatePayloadCreator,
        DeleteByIdPayloadCreator
    >,
): CreateEntityReturn<
    Entity,
    QueryAllPayloadCreator,
    QueryByIdPayloadCreator,
    UpdateByIdPayloadCreator,
    CreatePayloadCreator,
    DeleteByIdPayloadCreator
> {
    const idSelectorOrDefault = config.selectId ?? ((entity: any) => entity.id);
    const paginationEntityName = `${KeyNamespaces.ENTITY}/${config.name}`;
    const paginationEntityAdapter = createPaginationEntityAdapter<
        Entity,
        QueryAllPayloadCreator,
        QueryByIdPayloadCreator,
        UpdateByIdPayloadCreator,
        CreatePayloadCreator,
        DeleteByIdPayloadCreator
    >({
        config,
        paginationEntityName,
    });

    const selectAll = (): Entity[] =>
        paginationEntityAdapter.entityHelpers.selectEntitiesState()?.data ?? [];
    const selectSlice = (): EntityListSliceState<Entity> => {
        const state = paginationEntityAdapter.entityHelpers.selectEntitiesState();
        return {
            error: state.error ?? null,
            data: mapListToIndexedObject(state.data ?? [], idSelectorOrDefault),
            lastUpdated: state.lastUpdated,
            didInvalidate: state.didInvalidate,
            isPending: state.didInvalidate,
        };
    };
    const selectSliceById = (id: string): EntitySliceState<Entity> => {
        return paginationEntityAdapter.entityHelpers.selectEntityState(id);
    };

    const conditionQueryByIdAdapter:
        | AgnosticCondition<GetPayloadArg<QueryByIdPayloadCreator>>
        | undefined =
        config.conditionQueryById &&
        ((arg: GetPayloadArg<QueryByIdPayloadCreator>) =>
            config.conditionQueryById!(arg, selectSlice()));
    const conditionQueryAllAdapter:
        | AgnosticCondition<GetPayloadArg<QueryAllPayloadCreator>>
        | undefined =
        config.conditionQueryAll &&
        ((arg: GetPayloadArg<QueryAllPayloadCreator>) =>
            config.conditionQueryAll!(arg, selectSlice()));

    return {
        useEntities: buildUseEntities<QueryAllPayloadCreator, Entity>(
            idSelectorOrDefault,
            paginationEntityAdapter.useEntities,
            conditionQueryAllAdapter,
            paginationEntityAdapter.entityHelpers.invalidateLists,
        ),
        useEntity: buildUseEntity<QueryByIdPayloadCreator, Entity>(
            paginationEntityAdapter.useEntity,
            paginationEntityAdapter.entityHelpers.invalidate,
            conditionQueryByIdAdapter,
        ),
        useDeleteEntity: paginationEntityAdapter.useEntityDelete as UseDeleteEntity<
            GetPayloadCreatorReturnType<DeleteByIdPayloadCreator>,
            GetPayloadArg<DeleteByIdPayloadCreator, any>
        >,
        useUpdateEntity: paginationEntityAdapter.useEntityUpdate as UseUpdateEntity<
            Entity,
            GetPayloadArg<UpdateByIdPayloadCreator, any>
        >,
        useCreateEntity: paginationEntityAdapter.useEntityCreate,
        create: paginationEntityAdapter.entityHelpers.createEntity as Create<
            Entity,
            GetPayloadArg<QueryAllPayloadCreator, QueryParamsBaseType>
        >,
        deleteById: paginationEntityAdapter.entityHelpers
            .deleteEntity as DeleteById<DeleteByIdPayloadCreator>,
        updateById: paginationEntityAdapter.entityHelpers.updateEntity as UpdateById<Entity>,
        invalidate: () => {
            return paginationEntityAdapter.entityHelpers.invalidateAll();
        },
        invalidateById: (id: string) => {
            return paginationEntityAdapter.entityHelpers.invalidate(id);
        },
        queryAll: (arg) => {
            return paginationEntityAdapter.entityHelpers
                .refetchList(arg as GetPayloadArg<QueryAllPayloadCreator, any>)
                .then((result) => result.data ?? []);
        },
        queryAllIfNeeded: (arg): Promise<Entity[] | undefined> => {
            if (
                config.conditionQueryAll &&
                config.conditionQueryAll(
                    arg as GetPayloadArg<QueryAllPayloadCreator, any>,
                    selectSlice(),
                ) === false
            ) {
                return Promise.resolve(undefined);
            }
            return paginationEntityAdapter.entityHelpers
                .queryListIfNeeded(arg as GetPayloadArg<QueryAllPayloadCreator, any>)
                .then((result) => result.data ?? []);
        },
        queryById: (arg) => {
            return paginationEntityAdapter.entityHelpers.refetch(
                ((arg as any)?.id ?? arg) as string,
                arg as any,
            );
        },
        queryByIdIfNeeded: (arg): Promise<Entity | undefined> => {
            if (
                config.conditionQueryById &&
                config.conditionQueryById(arg, selectSlice()) === false
            ) {
                return Promise.resolve(undefined);
            }
            return paginationEntityAdapter.entityHelpers.queryIfNeeded(
                ((arg as any)?.id ?? arg) as string,
                arg as any,
            );
        },
        selectAll,
        selectById: (id) => {
            return paginationEntityAdapter.entityHelpers.selectEntityState(id)?.data;
        },
        selectEntities: () => {
            return mapListToIndexedObject(selectAll(), idSelectorOrDefault);
        },
        selectIds: () => {
            return selectAll().map((p) => idSelectorOrDefault(p));
        },
        selectSlice,
        selectSliceById,
        selectTotal: () => {
            return selectAll().length;
        },
        getListSliceId: (arg) =>
            paginationEntityAdapter.entityHelpers.getListSliceId(
                arg as GetPayloadArg<QueryAllPayloadCreator, any>,
            ),
        getDetailSliceId: (arg) => paginationEntityAdapter.entityHelpers.getDetailSliceId(arg),
        updateCache: paginationEntityAdapter.entityHelpers.updateCache,
        updateListCache: paginationEntityAdapter.entityHelpers.updateListCache,
    };
}
